/**
 * The plan's status: the guided one-click route, and a picker that can set any status.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY BOTH, AND NOT JUST THE PICKER
 * ──────────────────────────────────────────────────────────────────────────────
 * `plan.allowedTransitions` is the lifecycle map's own answer to "what is the next step",
 * and on a Draft that is one button — Propose. Replacing it with a ten-item dropdown would
 * make the everyday action cost a menu, and would stop the product having an opinion about
 * what comes next. So the buttons stay as the happy path, and the picker is the escape
 * hatch for everything the map cannot reach (six of the ten statuses have no inbound edge
 * until M5/M6, and a Won plan otherwise has nowhere to go but Lost).
 *
 * The picker IS the status badge beside the plan's name. It used to be a separate
 * "Set status…" select at the far end of a crowded action row, and planners looking at
 * the status at the top of the plan could not find how to change it. The badge is where
 * the eye already is, so that is where the control lives.
 *
 * Both routes go through the SAME endpoint and the same dialog. The server decides which is
 * which — see the header of src/api/plan-lifecycle.ts — so this file never has to reason
 * about the approval gate or the version freezes, only about which fields to collect.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY A DIALOG RATHER THAN window.prompt
 * ──────────────────────────────────────────────────────────────────────────────
 * Won needs two fields and Lost needs one, and a picker that fires three prompts in a row
 * is not a form. The dialog replaces the prompts the button row used to raise, so there is
 * one flow whichever control started it.
 */

import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { PlanStatusBadge, planStatusDotClass } from "@/components/plans/PlansTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTransitionResourcePlan } from "@/hooks/use-resource-plans";
import { planStatusLabel } from "@/lib/plan-status-labels";
import {
  isResourcePlanStatus,
  type ResourcePlanStatus,
  SELECTABLE_PLAN_STATUSES,
} from "@/lib/resource-plan-lifecycle";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

/** The verb the guided buttons use. Anything not listed is reached from the picker only. */
const TRANSITION_VERB: Partial<Record<ResourcePlanStatus, string>> = {
  Proposed: "Propose",
  Won: "Mark Won",
  Lost: "Mark Lost",
  Draft: "Reopen",
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * `canManageStatus` is OPTIONAL on the view (it postdates M4), so an older cached payload
 * falls back to `canEdit` — which is the pre-existing behavior, not a silent unlock.
 */
function canManageStatus(plan: ResourcePlanDetailView): boolean {
  return plan.canManageStatus ?? plan.canEdit;
}

function guidedTargets(plan: ResourcePlanDetailView): ResourcePlanStatus[] {
  return plan.allowedTransitions.filter(isResourcePlanStatus);
}

/**
 * The plan header's status pickers share one trigger shape — the `Select` trigger's
 * `sm` box — so Sales and Resourcing sit side by side as a matched pair.
 */
export const STATUS_TRIGGER_CLASS =
  "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30";

/** A status's color, as a dot beside its name. */
export function StatusDot({ className }: { className: string }) {
  return <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", className)} />;
}

/** The guided next-step buttons (Propose, Mark Won…) for the header's action row. */
export function PlanStatusControl({ plan }: { plan: ResourcePlanDetailView }) {
  const [target, setTarget] = useState<ResourcePlanStatus | null>(null);
  // Rejecting moved to "Raise Concern" (RP-34): a Blocking Concern returns the plan.
  if (!canManageStatus(plan)) return null;
  const guided = guidedTargets(plan);

  return (
    <>
      {guided.map((to) => (
        <Button
          key={to}
          size="sm"
          variant={to === "Proposed" || to === "Won" ? "default" : "outline"}
          onClick={() => setTarget(to)}
        >
          {TRANSITION_VERB[to] ?? `Mark ${planStatusLabel(to)}`}
        </Button>
      ))}
      {target !== null && (
        <StatusChangeDialog
          plan={plan}
          to={target}
          isOverride={false}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
}

/**
 * The status badge beside the plan's name, and the menu that changes it. Without
 * `canManageStatus` it is the plain badge — nothing to click means nothing that looks
 * clickable.
 */
export function PlanStatusMenu({ plan }: { plan: ResourcePlanDetailView }) {
  const [target, setTarget] = useState<ResourcePlanStatus | null>(null);
  const canLock = plan.canLock === true;
  // Locking and unlocking are RM/ADMIN (2026-10-14): a Locked plan is just a badge to
  // everyone else.
  if (!canManageStatus(plan) || (plan.status === "Locked" && !canLock)) {
    return (
      <span className={cn(STATUS_TRIGGER_CLASS, "cursor-default hover:bg-transparent")}>
        <span className="text-muted-foreground">Sales:</span>
        <StatusDot className={planStatusDotClass(plan.status)} />
        <span className="font-medium">{planStatusLabel(plan.status)}</span>
      </span>
    );
  }

  const current = isResourcePlanStatus(plan.status) ? plan.status : null;
  const guided = guidedTargets(plan);
  // A Locked plan only unlocks — back to the status it was locked from (2026-10-14).
  const lockedFrom =
    plan.status === "Locked" &&
    plan.lockedFromStatus != null &&
    isResourcePlanStatus(plan.lockedFromStatus)
      ? plan.lockedFromStatus
      : null;
  const direct =
    lockedFrom !== null
      ? [lockedFrom]
      : SELECTABLE_PLAN_STATUSES.filter(
          (s) => s !== current && !guided.includes(s) && (s !== "Locked" || canLock),
        );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Styled as the Resourcing picker beside it, so the two statuses read as a
              pair of controls rather than a pill and a field. */}
          <button
            type="button"
            className={STATUS_TRIGGER_CLASS}
            aria-label={`Sales status: ${planStatusLabel(plan.status)}. Change this plan's status`}
            title="The plan's sales status — separate from the Resource Manager's Resourcing status."
          >
            <span className="text-muted-foreground">Sales:</span>
            <StatusDot className={planStatusDotClass(plan.status)} />
            <span className="font-medium">{planStatusLabel(plan.status)}</span>
            <ChevronDown className="size-4 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[11rem]">
          {guided.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Next step
              </DropdownMenuLabel>
              {guided.map((s) => (
                <DropdownMenuItem key={s} onSelect={() => setTarget(s)}>
                  <PlanStatusBadge status={s} />
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {lockedFrom !== null ? "Unlock, back to" : "Set directly"}
          </DropdownMenuLabel>
          {direct.map((s) => (
            <DropdownMenuItem key={s} onSelect={() => setTarget(s)}>
              <PlanStatusBadge status={s} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {target !== null && (
        <StatusChangeDialog
          plan={plan}
          to={target}
          isOverride={!guided.includes(target)}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
}

export function StatusChangeDialog({
  plan,
  to,
  isOverride,
  onClose,
}: {
  plan: ResourcePlanDetailView;
  to: ResourcePlanStatus;
  isOverride: boolean;
  onClose: () => void;
}) {
  const transition = useTransitionResourcePlan(plan.id);

  const [wonDate, setWonDate] = useState(plan.wonDate ?? todayIso());
  const [sowReference, setSowReference] = useState(plan.sowReference ?? "");
  const [lostReason, setLostReason] = useState("");
  const [note, setNote] = useState("");

  /*
   * RP-17.1's note, collected on the way OUT rather than after the refusal. The planner
   * cannot know whether approval is needed — the gate decides that from cost they are not
   * shown — so it is offered on every Propose of a plan that looks below floor, and ignored
   * by the server when the gate does not fire.
   */
  const asksApprovalNote = !isOverride && to === "Proposed" && plan.marginStatus === "red";

  const missing =
    (to === "Won" && (wonDate.trim() === "" || sowReference.trim() === "")) ||
    (to === "Lost" && lostReason.trim() === "");

  function submit() {
    transition.mutate(
      {
        to,
        ...(to === "Won" ? { wonDate, sowReference } : {}),
        ...(to === "Lost" ? { lostReason } : {}),
        ...(asksApprovalNote ? { approvalNote: note.trim() === "" ? null : note } : {}),
        ...(isOverride ? { statusChangeReason: note.trim() === "" ? null : note } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Set status to</span>
            <PlanStatusBadge status={to} />
          </DialogTitle>
          <DialogDescription>
            {isOverride
              ? `"${plan.name}" is ${planStatusLabel(plan.status)}. Setting it to ${planStatusLabel(to)} records the change against your name without running the steps in between.`
              : asksApprovalNote
                ? `This plan is below the ${plan.marginFloorPercent}% floor, so proposing it asks Finance or an Admin to approve. It stays Open until they decide.`
                : `"${plan.name}" is currently ${planStatusLabel(plan.status)}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {to === "Won" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="plan-won-date">Won date</Label>
                <Input
                  id="plan-won-date"
                  type="date"
                  value={wonDate}
                  onChange={(e) => setWonDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plan-sow-reference">SOW reference</Label>
                <Input
                  id="plan-sow-reference"
                  value={sowReference}
                  onChange={(e) => setSowReference(e.target.value)}
                  placeholder="SOW-2026-014"
                />
              </div>
            </>
          )}

          {to === "Lost" && (
            <div className="space-y-1.5">
              <Label htmlFor="plan-lost-reason">Why was this lost?</Label>
              <Textarea
                id="plan-lost-reason"
                rows={3}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
            </div>
          )}

          {(isOverride || asksApprovalNote) && (
            <div className="space-y-1.5">
              <Label htmlFor="plan-status-note">
                {asksApprovalNote ? "Anything the approver should know?" : "Why?"}{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="plan-status-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              {isOverride && (
                <p className="text-xs text-muted-foreground">
                  Listed on the Changes tab with your name and the time.
                </p>
              )}
            </div>
          )}

          {isOverride && to === "HandedOver" && (
            <p className="rounded-md bg-warning-100 px-2.5 py-2 text-xs text-warning-700 dark:text-warning-400">
              This only changes the label. No Handover record is written and nobody is staffed onto
              the Pod — that is the real hand-over, and it is not built yet.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={transition.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={missing || transition.isPending}>
            {transition.isPending ? "Saving…" : "Set status"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
